import { useCallback, useMemo } from 'react';
import {
  useCreateContentStepVideoTokenMutation,
  useDeleteContentStepVideoMutation,
} from '@/entities/Courses';
import { GET_LESSONS, rtkApi } from '@/shared/api/rtkApi';
import { useAppDispatch } from '@/shared/lib/hooks/useAppDispatch';
import type { IStreamVideo } from '@/shared/types/common';
import type { StepVideoUploadSubject } from '../model/types';
import { videoUploads } from '../model/videoUploadsStore';
import { getResumableUploadUrl } from './videoState';

const isNotFound = (error: unknown) => (error as { status?: number })?.status === 404;

/** Запускает загрузку видео шага через менеджер, связывая его с API шагов */
export const useStepVideoUploader = () => {
  const dispatch = useAppDispatch();
  const [createContentStepVideoToken] = useCreateContentStepVideoTokenMutation();
  const [deleteContentStepVideo] = useDeleteContentStepVideoMutation();

  const start = useCallback(
    // subject — урок и номер шага для трея; currentVideo — видео шага, как его видит карточка:
    // тот же файл продолжает его загрузку
    (
      stepId: number,
      file: File,
      subject: StepVideoUploadSubject,
      currentVideo?: IStreamVideo | null,
    ) => {
      videoUploads.start(
        stepId,
        file,
        subject,
        {
          getUploadUrl: async (videoFile) => {
            const token = await createContentStepVideoToken({
              stepId,
              body: {
                fileName: videoFile.name,
                fileSize: videoFile.size,
                // Вместе с именем и размером — отпечаток файла для докачки после перезагрузки
                fileLastModified: videoFile.lastModified,
              },
            }).unwrap();
            const uploadURL = token.data?.uploadURL;
            if (!uploadURL) throw new Error('Failed to prepare upload. Please try again.');
            return uploadURL;
          },
          removeVideo: async () => {
            try {
              await deleteContentStepVideo(stepId).unwrap();
            } catch (error) {
              if (!isNotFound(error)) throw error;
            }
          },
          // Байты ушли в Stream напрямую, поэтому урок перечитывается: шаг покажет обработку
          onUploaded: () => {
            dispatch(rtkApi.util.invalidateTags([GET_LESSONS]));
          },
        },
        { resumeUrl: getResumableUploadUrl(currentVideo, file) },
      );
    },
    [createContentStepVideoToken, deleteContentStepVideo, dispatch],
  );

  return useMemo(
    () => ({
      start,
      cancel: videoUploads.cancel,
      retry: videoUploads.retry,
      forget: videoUploads.forget,
    }),
    [start],
  );
};
